import * as React from 'react';
import { t } from 'i18next';

import { InputBox } from '../../InputBox';
import { FloorPlanSubPage, MouseCoords, DragType, FloorPlanSubPageProps, FloorPlanSubPageState } from './FloorPlanSubPage';
import { LayoutType, BaseColor, LoupeSize, LoupeInViewSize } from '../../../constants/LayoutConstants';
import { AppConfigurationStore } from '../../../stores/AppConfigurationStore';
import { FloorPlanStore } from '../../../stores/FloorPlanStore';
import { ProjectSettingsStore } from '../../../stores/ProjectSettingsStore';

export class FloorPlanSetSize extends FloorPlanSubPage {
    public inputBox: InputBox;

    protected baseZoom: number = 0;
    protected baseDistance: number = 0;
    protected boundActions: boolean = false;
    protected inputContainer: HTMLDivElement;

    get zoom(): number {return this.props.store.sizeZoom; }
    set zoom(value: number) {this.props.store.sizeZoom = value; }

    get imagePosition(): MouseCoords {return this.props.store.sizeImagePosition; }
    set imagePosition(value: MouseCoords) {this.props.store.sizeImagePosition = value; }

    public componentWillMount(): void {
        this.addStoreListener(FloorPlanStore, this.onChange);
        this.addStoreListener(ProjectSettingsStore, this.onChange);
        this.onChange();
    }

    public componentDidUpdate(prevProps: FloorPlanSubPageProps, prevState: FloorPlanSubPageState): void {
        if (prevProps.pageIndex === 2 && this.props.pageIndex !== 2) {
            this.updateDimensions();
        }
        if (
            this.imageCanvas != null &&
            this.state.image_id != null &&
            this.currentImage !== this.state.image_id
        ) {
            const ctx: CanvasRenderingContext2D | null = this.imageCanvas.getContext('2d');
            if (ctx == null) {
                return;
            }
            ctx.clearRect(0, 0, this.imageCanvas.width, this.imageCanvas.height);
            this.setZoom(
                Math.min(
                    (this.imageCanvas.width / FloorPlanStore.image.width),
                    (this.imageCanvas.height / FloorPlanStore.image.height),
                    (this.imageCanvas.width / FloorPlanStore.image.height),
                    (this.imageCanvas.height / FloorPlanStore.image.width)
                ),
                null
            );
            const newPos: MouseCoords = this.centerImage(
                this.imageCanvas,
                FloorPlanStore.image.width,
                FloorPlanStore.image.height,
                this.zoom
            );
            this.props.store.setSizeImagePosition(newPos.x, newPos.y);
            this.props.store.arrowPositions = [
                this.getRelativePosition({x: this.imageCanvas.width * .5 - 100, y: 50}),
                this.getRelativePosition({x: this.imageCanvas.width * .5 + 100, y: 50}),
            ];
            this.redraw();
            this.currentImage = this.state.image_id;
        }

        if (this.boundActions && this.props.pageIndex !== 0) {
            this.unbindActions();
        } else if (!this.boundActions && this.props.pageIndex === 0) {
            this.bindActions();
        }
        if (this.props.pageIndex === 1 && prevProps.pageIndex === 0 && this.inputBox != null) {
            this.inputBox.focus();
        }
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.onResize, false);
        super.componentWillUnmount();
        if (this.resizeTrigger) {
            window.clearTimeout(this.resizeTrigger);
        }
        if (this.props.pageIndex === 0) {
            this.unbindActions();
        }
    }

    public componentDidMount(): void {
        window.addEventListener('resize', this.onResize, false);
        if (this.props.pageIndex === 0) {
            this.bindActions();
        }
        this.updateDimensions();
        this.onChange();
    }

    public render(): JSX.Element | null {
        return (
            <div
                className="fp-canvas"
                ref={this.setDivElement}
                style={{
                    display: (this.props.pageIndex === 0 || this.props.pageIndex === 1) ? 'block' : 'none',
                    overflow: (this.props.pageIndex === 0) ? 'hidden' : 'visible'
                }}
            >
                <div className={'interactor-fp' + (this.props.pageIndex === 0 ? ' with-circle' : '')} id="left-arrow"/>
                <div className={'interactor-fp' + (this.props.pageIndex === 0 ? ' with-circle' : '')} id="right-arrow"/>
                <div
                    className="fp-input"
                    style={this.props.pageIndex === 1 ? {opacity: 1} : {opacity: 0, pointerEvents: 'none'}}
                    ref={(node: HTMLDivElement) => { this.inputContainer = node; }}
                >
                    <InputBox
                        className=""
                        noValidIndication={true}
                        doubleBorder={true}
                        defaultValue={''}
                        changeCallback={this.scaleChange}
                        noX={true}
                        inputType="number"
                        submitCallback={this.props.submitCallback}
                        ref={(component: InputBox) => { this.inputBox = component; }}
                        infoLabel={t('Length in {{unit}}', {unit: this.state.unitString})}
                    />
                </div>
                <canvas style={{zIndex: 2}} ref={(node: HTMLCanvasElement) => { this.overlayCanvas = node; }}/>
                <canvas
                    style={{zIndex: 1, opacity: (this.props.pageIndex === 0 ? 1 : .3)}}
                    ref={(node: HTMLCanvasElement) => { this.imageCanvas = node; }}
                />
            </div>
        );
    }

    protected updateDimensions = (): void => {
        const width: number = this.divElement.clientWidth;
        const height: number = this.divElement.clientHeight;
        if (
            width != null &&
            height != null &&
            (
                this.imageCanvas.width !== width ||
                this.imageCanvas.height !== height ||
                this.imageCanvas.width !== this.overlayCanvas.width ||
                this.imageCanvas.height !== this.overlayCanvas.height
            )
        ) {
            this.imageCanvas.width = this.overlayCanvas.width = width;
            this.imageCanvas.height = this.overlayCanvas.height = height;
            if (this.props.store.arrowPositions.length !== 2) {
                this.props.store.arrowPositions = [
                    {x: width * .5 - 100, y: 50},
                    {x: width * .5 + 100, y: 50}
                ];
            }

            const widthRatio: number = Math.max(
                this.props.store.arrowPositions[0].x * this.zoom + this.imagePosition.x,
                this.props.store.arrowPositions[1].x * this.zoom + this.imagePosition.x,
                width
            ) / width;
            const heightRatio: number = Math.max(
                this.props.store.arrowPositions[0].y * this.zoom + this.imagePosition.y,
                this.props.store.arrowPositions[1].y * this.zoom + this.imagePosition.y,
                height
            ) / height;
            if (Math.max(widthRatio, heightRatio) > 1) {
                this.setZoom(this.zoom / Math.max(widthRatio, heightRatio), {x: 0, y: 0}, true);
            } else if (this.zoom === this.beforeZoomConfig.new) {
                this.setZoom(this.beforeZoomConfig.old, {x: 0, y: 0});
            }
        }
    };

    protected updateActiveClass(): void {
        if (this.draggingInfo.index === DragType.LEFT) {
            jQuery('#left-arrow').addClass('active');
        } else {
            jQuery('#left-arrow').removeClass('active');
        }

        if (this.draggingInfo.index === DragType.RIGHT) {
            jQuery('#right-arrow').addClass('active');
        } else {
            jQuery('#right-arrow').removeClass('active');
        }
    }

    protected getPixelPosition = (position: number, minValue?: number): number => {
        if (minValue != null) {
            return Math.max(0, position * this.zoom);
        }
        return position * this.zoom;
    };

    protected setInputPosition(): void {
        if (this.inputContainer && this.inputContainer.parentElement) {
            const bounding: ClientRect = this.inputContainer.getBoundingClientRect();
            const boundingParent: ClientRect = this.inputContainer.parentElement.getBoundingClientRect();
            let middleX: number = (
                this.props.store.arrowPositions[0].x * this.zoom +
                (this.props.store.arrowPositions[1].x * this.zoom - this.props.store.arrowPositions[0].x * this.zoom) * .5
            ) + this.imagePosition.x;
            if (this.props.store.arrowPositions[0].x * this.zoom + this.imagePosition.x <= 0) {
                middleX = (this.props.store.arrowPositions[1].x * this.zoom + this.imagePosition.x) * .5;
            } else if (this.props.store.arrowPositions[1].x * this.zoom + this.imagePosition.x <= 0) {
                middleX = (this.props.store.arrowPositions[0].x * this.zoom + this.imagePosition.x) * .5;
            }

            let middleY: number = (
                this.props.store.arrowPositions[0].y +
                (this.props.store.arrowPositions[1].y - this.props.store.arrowPositions[0].y) * .5
            ) * this.zoom + this.imagePosition.y;

            if (this.props.store.arrowPositions[0].y * this.zoom + this.imagePosition.y <= 0) {
                middleY = (this.props.store.arrowPositions[1].y * this.zoom + this.imagePosition.y) * .5;
            } else if (this.props.store.arrowPositions[1].y * this.zoom + this.imagePosition.y <= 0) {
                middleY = (this.props.store.arrowPositions[0].y * this.zoom + this.imagePosition.y) * .5;
            }

            let inputLeft: number = middleX - .5 * bounding.width;
            let inputTop: number = middleY - .5 * bounding.height;

            if (inputLeft < 0) {
                inputLeft = 0;
            } else if (inputLeft + bounding.width > boundingParent.width) {
                inputLeft = (boundingParent.width / 2) - (.5 * bounding.width);
            }

            if (inputTop < 0 || inputTop + bounding.height > boundingParent.height) {
                inputTop = 2.5 * bounding.height;
            }

            this.inputContainer.style.left = (inputLeft).toString() + 'px';
            this.inputContainer.style.top = (inputTop).toString() + 'px';

            // Make sure inputbox is not placed behind virtual keyboard even if automated panning does not work
            if (this.props.layoutType === LayoutType.SMALL && (window.innerHeight - bounding.bottom) < .5 * window.innerHeight) {
                const diffH: number = .5 * window.innerHeight - (window.innerHeight - bounding.bottom);
                inputTop -= diffH;
                this.inputContainer.style.top = (inputTop).toString() + 'px';
            }

            // Make sure inputbox is horizontal completely visible
            if ((window.innerWidth - (bounding.left + bounding.width + 20)) < 0) {
                const diffW: number = -(window.innerWidth - (bounding.left + bounding.width + 20));
                inputLeft -= diffW;
                this.inputContainer.style.left = (inputLeft).toString() + 'px';
            }

            if (this.inputBox != null) {
                this.inputBox.updateTooltipPosition();
            }
        }
    }

    protected redrawInteractors(): void {
        if (this.overlayCanvas != null) {
            const ctx: CanvasRenderingContext2D = this.overlayCanvas.getContext('2d')!;
            ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
            if (this.props.store.arrowPositions.length === 2) {
                this.setInputPosition();
                const angleDeg: number = Math.atan2(
                    this.props.store.arrowPositions[1].y - this.props.store.arrowPositions[0].y,
                    this.props.store.arrowPositions[1].x - this.props.store.arrowPositions[0].x
                ) * 180 / Math.PI;

                this.props.store.arrowPositions.map((element, index) => {
                    jQuery('.interactor-fp')[index].style.pointerEvents = (
                        (this.draggingInfo.index === DragType.IMAGE || this.draggingInfo.index === DragType.ZOOM) ?
                        'none' : 'auto'
                    );
                    jQuery('.interactor-fp')[index].style.left = (
                        element.x * this.zoom + this.imagePosition.x - .5 * jQuery('.interactor-fp')[index].clientWidth
                    ).toString() + 'px';
                    jQuery('.interactor-fp')[index].style.top = (
                        element.y * this.zoom + this.imagePosition.y - .5 * jQuery('.interactor-fp')[index].clientHeight
                    ).toString() + 'px';
                    jQuery('.interactor-fp')[index].style.transform = 'rotate(' + (index === 0 ? angleDeg : angleDeg + 180).toString() + 'deg)';
                });
                ctx.beginPath();
                ctx.moveTo(
                    this.props.store.arrowPositions[0].x * this.zoom + this.imagePosition.x,
                    this.props.store.arrowPositions[0].y * this.zoom + this.imagePosition.y
                );
                ctx.lineTo(
                    this.props.store.arrowPositions[1].x * this.zoom + this.imagePosition.x,
                    this.props.store.arrowPositions[1].y * this.zoom + this.imagePosition.y
                );
                ctx.strokeStyle = BaseColor;
                ctx.lineWidth = 2;
                ctx.stroke();
                // set "store"-properties for use in other wizard-page
                this.props.store.pixelDistance = this.getDistance(this.props.store.arrowPositions[0], this.props.store.arrowPositions[1]);
                this.props.store.angle = angleDeg;
            }
        }
    }

    protected redrawLoupe(): void {
        const canvasLoupe: HTMLCanvasElement = document.getElementById('fp-loupe') as HTMLCanvasElement;
        if (
            this.imageCanvas != null &&
            canvasLoupe != null &&
            this.state != null &&
            (this.draggingInfo.index === DragType.LEFT || this.draggingInfo.index === DragType.RIGHT)
        ) {
            const canvasBoundingRect: ClientRect = this.imageCanvas.getBoundingClientRect();
            const ctxImage: CanvasRenderingContext2D = this.imageCanvas.getContext('2d')!;
            let imageData: ImageData;
            if (this.draggingInfo.index === DragType.LEFT) {
                imageData = ctxImage.getImageData(
                    this.props.store.arrowPositions[0].x * this.zoom + this.imagePosition.x - .5 * LoupeSize,
                    this.props.store.arrowPositions[0].y * this.zoom + this.imagePosition.y - .5 * LoupeSize,
                    LoupeSize,
                    LoupeSize
                );
                canvasLoupe.style.left = (
                    this.props.store.arrowPositions[0].x * this.zoom +
                    canvasBoundingRect.left +
                    this.imagePosition.x -
                    .5 * canvasLoupe.width).toString() +
                    'px';
                canvasLoupe.style.top = (
                    this.props.store.arrowPositions[0].y * this.zoom +
                    canvasBoundingRect.top +
                    this.imagePosition.y -
                    1.5 * canvasLoupe.height).toString() +
                    'px';
            } else {
                imageData = ctxImage.getImageData(
                    this.props.store.arrowPositions[1].x * this.zoom + this.imagePosition.x - .5 * LoupeSize,
                    this.props.store.arrowPositions[1].y * this.zoom + this.imagePosition.y - .5 * LoupeSize,
                    LoupeSize,
                    LoupeSize
                );
                canvasLoupe.style.left = (
                    this.props.store.arrowPositions[1].x * this.zoom +
                    canvasBoundingRect.left +
                    this.imagePosition.x -
                    .5 * canvasLoupe.width).toString() +
                    'px';
                canvasLoupe.style.top = (
                    this.props.store.arrowPositions[1].y * this.zoom +
                    canvasBoundingRect.top +
                    this.imagePosition.y -
                    1.5 * canvasLoupe.height).toString() +
                    'px';
            }
            const ctxLoupe: CanvasRenderingContext2D = canvasLoupe.getContext('2d')!;
            const tempCanvas: HTMLCanvasElement = document.createElement('canvas') as HTMLCanvasElement;
            tempCanvas.width = LoupeSize;
            tempCanvas.height = LoupeSize;
            tempCanvas.getContext('2d')!.putImageData(imageData, 0, 0);
            ctxLoupe.fillStyle = 'white';
            ctxLoupe.fillRect(0, 0, canvasLoupe.width, canvasLoupe.height);
            ctxLoupe.drawImage(tempCanvas, 0, 0, 80, 80);
            ctxLoupe.beginPath();
            ctxLoupe.strokeStyle = BaseColor;
            const basePosition: number = .5 * LoupeInViewSize;
            ctxLoupe.moveTo(basePosition, basePosition);
            ctxLoupe.lineWidth = 2 * LoupeInViewSize / LoupeSize;
            if (this.draggingInfo.index === DragType.LEFT) {
                ctxLoupe.lineTo(
                    basePosition + .5 * LoupeInViewSize * Math.cos(Math.PI * this.props.store.angle / 180.0),
                    basePosition + .5 * LoupeInViewSize * Math.sin(Math.PI * this.props.store.angle / 180.0)
                );
            } else {
                ctxLoupe.lineTo(
                    basePosition + .5 * LoupeInViewSize * Math.cos(Math.PI * (this.props.store.angle + 180) / 180.0),
                    basePosition + .5 * LoupeInViewSize * Math.sin(Math.PI * (this.props.store.angle + 180) / 180.0)
                );
            }
            ctxLoupe.moveTo(basePosition, basePosition);
            ctxLoupe.lineTo(
                basePosition + .25 * LoupeInViewSize * Math.cos(Math.PI * (this.props.store.angle + 90) / 180.0),
                basePosition + .25 * LoupeInViewSize * Math.sin(Math.PI * (this.props.store.angle + 90) / 180.0)
            );
            ctxLoupe.moveTo(basePosition, basePosition);
            ctxLoupe.lineTo(
                basePosition + .25 * LoupeInViewSize * Math.cos(Math.PI * (this.props.store.angle - 90) / 180.0),
                basePosition + .25 * LoupeInViewSize * Math.sin(Math.PI * (this.props.store.angle - 90) / 180.0)
            );
            ctxLoupe.stroke();
            canvasLoupe.style.display = 'block';
        } else if (canvasLoupe != null) {
            canvasLoupe.style.display = 'none';
        }
    }

    protected redraw = (noImage?: boolean): void => {
        this.updateDimensions();
        this.updateActiveClass();
        if (!noImage) {
            this.redrawImage(false);
        }
        this.redrawInteractors();
        if (AppConfigurationStore.getState().touchDevice) {
            this.redrawLoupe();
        }
    };

    protected pointerMove = (event: PointerEvent): void => {
        if (
            this.draggingInfo.index != null &&
            this.props.pageIndex === 0
        ) {
            const coords: {x: number, y: number} = this.getPointerCoords(event);
            if (
                (this.draggingInfo.index === DragType.LEFT || this.draggingInfo.index === DragType.RIGHT) &&
                event.pointerId === this.draggingInfo.pointerId
            ) {
                const arrowPosition: MouseCoords = this.getRelativePosition(coords);
                if (this.draggingInfo.index === DragType.LEFT && this.props.store.arrowPositions[0] != null) {
                    this.props.store.arrowPositions[0] = {
                        x: arrowPosition.x - this.draggingInfo.x,
                        y: arrowPosition.y - this.draggingInfo.y
                    };
                } else if (this.draggingInfo.index === DragType.RIGHT && this.props.store.arrowPositions[1] != null) {
                    this.props.store.arrowPositions[1] = {
                        x: arrowPosition.x - this.draggingInfo.x,
                        y: arrowPosition.y - this.draggingInfo.y
                    };
                }
            } else {
                this.basePointerMove(event);
            }
            this.draggingInfo.moved = true;
            this.redraw(this.draggingInfo.index !== DragType.IMAGE && this.draggingInfo.index !== DragType.ZOOM);
        }
    };

    protected onChange = (): void => {
        const image: string = FloorPlanStore.getState().image_id;
        if (this.state == null || image !== this.state.image_id) {
            this.setState({
                image_id: FloorPlanStore.getState().image_id,
                angle: 0,
                unitString: ProjectSettingsStore.getState().unitOfMeasurement
            });
        }
    };

    protected pointerUp = (event: PointerEvent): void => {
        if (
            this.props.pageIndex === 0 &&
            this.props.store.arrowPositions.length === 2 &&
            this.draggingInfo != null &&
            this.draggingInfo.index === DragType.IMAGE &&
            !this.draggingInfo.moved
        ) {
            const coords: MouseCoords = this.getPointerCoords(event);
            const relativeCoords: MouseCoords = this.getRelativePosition(coords);
            const distanceToLeftArrow: number = this.getDistance(relativeCoords, this.props.store.arrowPositions[0]);
            const distanceToRightArrow: number = this.getDistance(relativeCoords, this.props.store.arrowPositions[1]);
            if (distanceToLeftArrow < distanceToRightArrow) {
                this.props.store.arrowPositions[0] = relativeCoords;
            } else {
                this.props.store.arrowPositions[1] = relativeCoords;
            }
        }
        if (this.zoomInfo.pointerId === event.pointerId && this.draggingInfo.index === DragType.ZOOM) {
            this.draggingInfo.index = DragType.IMAGE;
            this.redraw();
        } else {
            this.draggingInfo.index = null;
            this.redraw(this.draggingInfo.index !== DragType.ZOOM && this.draggingInfo.index !== DragType.IMAGE);
        }
    };

    protected bindActions(): void {
        window.addEventListener('pointerup', this.pointerUp, true);
        this.divElement.setAttribute('touch-action', 'none');
        this.divElement.addEventListener('pointerdown', this.pointerDown, true);
        this.divElement.addEventListener('pointermove', this.pointerMove, true);
        this.divElement.addEventListener('wheel', this.mouseWheel, true);
        this.divElement.addEventListener('contextmenu', this.contextMenu, false);
        this.boundActions = true;
    }

    protected unbindActions(): void {
        window.removeEventListener('pointerup', this.pointerUp, true);
        this.divElement.removeEventListener('pointerdown', this.pointerDown, true);
        this.divElement.removeEventListener('pointermove', this.pointerMove, true);
        this.divElement.removeEventListener('wheel', this.mouseWheel, true);
        this.divElement.removeEventListener('contextmenu', this.contextMenu, false);
        this.boundActions = false;
    }

    protected scaleChange = (value: number | null): void => {
        if (value != null) {
            this.props.store.scaleDistance = value;
        }
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/overlay/floorplan/FloorPlanSetSize.tsx